import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
    Users01,
    UserCheck01,
    UserX01,
    TrendUp01,
    Plus,
    Eye
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { accountService } from '@/data/mock-accounts';
import type { Account, AccountStats } from '@/types/account';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    trend?: {
        value: number;
        isPositive: boolean;
    };
}

const StatCard = ({ title, value, icon: Icon, color, trend }: StatCardProps) => {
    const colorClasses = {
        brand: 'bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200',
        success: 'bg-utility-success-50 text-utility-success-700 ring-utility-success-200',
        warning: 'bg-utility-warning-50 text-utility-warning-700 ring-utility-warning-200',
        error: 'bg-utility-error-50 text-utility-error-700 ring-utility-error-200'
    };

    return (
        <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${colorClasses[color]}`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-tertiary">{title}</p>
                    <p className="text-3xl font-semibold text-primary mt-1">{value}</p>
                    {trend && (
                        <div className="flex items-center mt-2">
                            <TrendUp01
                                className={`size-4 mr-1 ${trend.isPositive ? 'text-success-primary' : 'text-error-primary rotate-180'}`}
                            />
                            <span className={`text-sm font-medium ${trend.isPositive ? 'text-success-primary' : 'text-error-primary'}`}>
                                {trend.isPositive ? '+' : ''}{trend.value}%
                            </span>
                        </div>
                    )}
                </div>
                <div className="rounded-lg bg-primary p-3">
                    <Icon className="size-6 text-fg-quaternary" />
                </div>
            </div>
        </div>
    );
};

export const DashboardPage = () => {
    const { t } = useTranslation(['pages:dashboard', 'common']);
    const [stats, setStats] = useState<AccountStats>({
        total: 0,
        active: 0,
        suspended: 0,
        inactive: 0,
        newThisMonth: 0
    });
    const [recentAccounts, setRecentAccounts] = useState<Account[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 500));

            const accountStats = accountService.getStats();
            const recent = accountService.getRecent(5);

            setStats(accountStats);
            setRecentAccounts(recent);
            setIsLoading(false);
        };

        loadData();
    }, []);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'Attivo':
                return <Badge color="success" size="sm">{t('admin.recentAccounts.status.active')}</Badge>;
            case 'Sospeso':
                return <Badge color="warning" size="sm">{t('admin.recentAccounts.status.suspended')}</Badge>;
            case 'Inattivo':
                return <Badge color="error" size="sm">{t('admin.recentAccounts.status.inactive')}</Badge>;
            default:
                return <Badge color="gray" size="sm">{status}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('admin.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('admin.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('admin.subtitle')}
                    </p>
                </div>
                <Link
                    to="/admin/accounts/new"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <Plus className="size-5" />
                    {t('admin.newAccount')}
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title={t('admin.stats.totalAccounts')}
                    value={stats.total}
                    icon={Users01}
                    color="brand"
                    trend={{ value: 12, isPositive: true }}
                />
                <StatCard
                    title={t('admin.stats.activeAccounts')}
                    value={stats.active}
                    icon={UserCheck01}
                    color="success"
                    trend={{ value: 8, isPositive: true }}
                />
                <StatCard
                    title={t('admin.stats.newThisMonth')}
                    value={stats.newThisMonth}
                    icon={TrendUp01}
                    color="brand"
                    trend={{ value: 15, isPositive: true }}
                />
                <StatCard
                    title={t('admin.stats.suspendedAccounts')}
                    value={stats.suspended + stats.inactive}
                    icon={UserX01}
                    color="warning"
                    trend={{ value: 3, isPositive: false }}
                />
            </div>

            {/* Recent Accounts Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('admin.recentAccounts.title')}
                    description={t('admin.recentAccounts.description')}
                    contentTrailing={
                        <Link
                            to="/admin/accounts"
                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                        >
                            <Eye className="size-4" />
                            {t('admin.recentAccounts.viewAll')}
                        </Link>
                    }
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('admin.recentAccounts.headers.userType')} isRowHeader />
                        <Table.Head label={t('admin.recentAccounts.headers.legalName')} />
                        <Table.Head label={t('admin.recentAccounts.headers.entityType')} />
                        <Table.Head label={t('admin.recentAccounts.headers.country')} />
                        <Table.Head label={t('admin.recentAccounts.headers.status')} />
                        <Table.Head label={t('admin.recentAccounts.headers.createdAt')} />
                    </Table.Header>
                    <Table.Body>
                        {recentAccounts.map((account) => (
                            <Table.Row key={account.id}>
                                <Table.Cell>
                                    <Badge color={account.userType === 'SHIPPER' ? 'blue' : 'success'} size="sm">
                                        {account.userType}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <div>
                                        <div className="font-medium text-primary">{account.legalName}</div>
                                        <div className="text-sm text-tertiary">{account.email}</div>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{account.entityType}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{account.country}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {getStatusBadge(account.status)}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {account.createdAt.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>
        </div>
    );
};
